import {
  faCheckSquare,
  faEye,
  faEyeSlash,
  faPaperclip,
  faSquare,
  faSquareMinus
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormLabel,
  IconButton,
  Input,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField
} from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import { useAtom } from 'jotai';
import get from 'lodash/get';
import { type ChangeEvent, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { YEAR_MONTH_DAY_TIME_FORMAT2 } from '@/constants/date.constants.ts';
import type { DynamicFieldData, Rule } from '@/features/SourceCreate';
import { useAzureConnectionString } from '@/features/SourceCreate/CloudSourceHooks/useAzureConnectionString';
import SOURCE_TYPE, {
  SOURCE_TYPES_SUPPORTING_FILE_UPLOADS
} from '@/features/SourceCreate/SourceCreate.constants';
import {
  selectedSourceDetailsAtom,
  selectedSourceTypeIdAtom,
  selectedSubscriptionTypeAtom,
  selectedTimeIntervalAtom,
  sourceDialogModeAtom
} from '@/features/SourceCreate/SourceCreate.store';
import { validateUrl } from '@/features/SourceCreate/SourceCreate.utils';
import { formatSourceName } from '@/pages/Administration/Devices/Devices.service';
import { isValidEmail } from '@/utils/validation.utils';

import type { FileDetails } from '../FileUploader';
import countryNames from './DynamicFormControl.constants';
import useStyles from './DynamicFormControl.styles';
import type { AutocompleteOption } from './DynamicFormControl.types';

// Constant for "Select All" option in autocomplete
const SELECT_ALL_OPTION = '__SELECT_ALL__';

const getAutocompleteOptionValue = (option: AutocompleteOption) => {
  return typeof option === 'string' ? option : option.value;
};

const getAutocompleteOptionLabel = (option: AutocompleteOption): string => {
  if (typeof option === 'string') {
    return option;
  }

  return option.label || option.value;
};

const DynamicFormControl = ({
  type,
  name,
  value,
  label,
  disabled,
  nonMandatory,
  retention,
  msg,
  config,
  options = [],
  validations,
  note,
  softExpiry,
  showSelectAll = true,
  onFieldChange
}: DynamicFieldData) => {
  const { classes } = useStyles();
  const {
    register,
    control,
    formState,
    setError,
    clearErrors,
    trigger,
    setValue,
    getValues
  } = useFormContext();
  const [showPassword, setShowPassword] = useState(false);
  const [mode] = useAtom(sourceDialogModeAtom);
  const [selectedSourceDetails] = useAtom(selectedSourceDetailsAtom);
  const [, setSelectedSubscriptionType] = useAtom(selectedSubscriptionTypeAtom);
  const [, setSelectedTimeInterval] = useAtom(selectedTimeIntervalAtom);
  const [selectedSourceTypeId] = useAtom(selectedSourceTypeIdAtom);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };
  const [selectedFile, setSelectedFile] = useState<FileDetails | null>(null);
  const [inputValue, setInputValue] = useState<string | number | string | null>(
    '' as string | number | null
  );

  // Watch for changes to storage_acct_conn_string field
  const storageConnectionString = useWatch({
    control,
    name: 'storage_acct_conn_string'
  });

  // Handle Azure-specific connection string logic
  const { parseAzureConnectionString, isAzureSource } =
    useAzureConnectionString({
      name,
      sourceTypeId: selectedSourceTypeId,
      mode,
      storageConnectionString:
        selectedSourceTypeId === SOURCE_TYPE.AZURE_EVENT_HUB
          ? storageConnectionString
          : undefined,
      setValue,
      trigger
    });

  let fieldType: string = type;

  // Setting the type of the input field from text to select for country attribute to avoid input errors
  if (name === 'country') {
    fieldType = 'country-dropdown';
  }

  const attachFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFile: FileDetails | null = event.target.files
      ? event.target.files[0]
      : null;
    if (newFile === null) return;
    if (newFile.safe === undefined) {
      newFile.safe = false;
    }
    setSelectedFile(newFile);
    setInputValue(newFile.name);
  };

  // Calls trigger to avoid showing an error message on file selection.
  useEffect(() => {
    if (selectedFile !== null) {
      trigger(name);
    }
  }, [selectedFile, trigger, name]);

  const validateFile = () => {
    // Source Type: Google Workspace
    if (
      mode === 'Edit' &&
      SOURCE_TYPES_SUPPORTING_FILE_UPLOADS.includes(
        selectedSourceDetails?.source_type_id
      ) &&
      name === 'file_upload'
    ) {
      if (selectedFile === null) {
        return true;
      }
    }

    let msge: boolean | string = true;
    if (
      validations &&
      validations.length > 0 &&
      validations[0]?.type === 'fileValidation' &&
      validations[0]?.rules
    ) {
      validations[0]?.rules.forEach((v: Rule) => {
        if (msge !== true) return; // stop checking once an error is found

        if (v.type === 'required') {
          if (selectedFile === null) {
            msge = v.message;
          }
        } else if (v.type === 'fileType') {
          const acceptedFormats = v.value || [];
          if (selectedFile) {
            const fileNameParts = selectedFile.name?.split('.') || [];
            const fileExtension =
              fileNameParts.length > 1
                ? fileNameParts[fileNameParts.length - 1]?.toLowerCase()
                : '';
            if (!acceptedFormats.includes(fileExtension)) {
              msge = v.message;
            }
          }
        } else if (v.type === 'fileSize') {
          const selFileSize = selectedFile?.size || 0;
          const fileSize: number = selFileSize / (1024 * 1024) || 0;
          const maxVal = v?.value || 0;
          if (fileSize > Number(maxVal)) {
            msge = v.message;
          }
        }
      });
    }
    return msge;
  };

  // All Dynamic dropdown changes are handled here
  const handleDropdownChange = (controlName: string, controlValue: string) => {
    // Subscription Type stored in state to handle enable/ disable category based on subscription type for Microsoft Defender Source Type
    if (controlName === 'subscription_type') {
      setSelectedSubscriptionType(controlValue);
    } else if (controlName === 'time_interval') {
      setSelectedTimeInterval(controlValue);
    }
  };

  useEffect(() => {
    // Source Type: Google Workspace
    if (
      mode === 'Edit' &&
      SOURCE_TYPES_SUPPORTING_FILE_UPLOADS.includes(
        selectedSourceDetails?.source_type_id
      ) &&
      name === 'file_upload'
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInputValue(selectedSourceDetails?.file_name || '');
    }
  }, [selectedSourceDetails]);

  switch (fieldType) {
    case 'text':
      if (
        name === 'api_url' ||
        name === 'base_url' ||
        name === 'authorize_url'
      ) {
        return (
          <TextField
            sx={{ width: '100%' }}
            type="text"
            disabled={disabled}
            error={!!get(formState.errors, name)}
            {...register(name, {
              required: nonMandatory
                ? false
                : `${formatSourceName(name)} is Required`,
              validate: validateUrl
            })}
          />
        );
      }

      if (name === 'site_name') {
        return null;
      }
      if (name === 'service_detail_name') {
        return null;
      }
      if (name.includes('.prefix')) {
        return (
          <TextField
            sx={{ width: '100%' }}
            type="text"
            disabled={disabled}
            error={!!get(formState.errors, name)}
            {...register(name, {
              required: nonMandatory ? false : `Prefix is Required`,
              validate: (prefixValue) => {
                if (!prefixValue) return true; // non-mandatory field

                if (prefixValue === '/') {
                  return "Prefix cannot be just '/'.";
                }

                if (prefixValue.startsWith('/')) {
                  return "Prefix should not start with '/'.";
                }

                return true;
              }
            })}
          />
        );
      }
      return (
        <TextField
          sx={{
            width: '100%',
            '& .MuiFormHelperText-root': {
              color: 'text.secondary'
            }
          }}
          type="text"
          helperText={note || ''}
          disabled={disabled}
          error={!!get(formState.errors, name)}
          {...register(name, config)}
        />
      );

    case 'hidden':
      return <input type="hidden" {...register(name, {})} />;

    case 'textarea':
      return (
        <TextField
          {...register(name, config)}
          multiline
          rows={3}
          fullWidth
          variant="outlined"
          sx={{
            '& div.MuiInputBase-formControl': {
              padding: '0.5rem!important',
              paddingRight: '0.75rem!important',
              paddingLeft: '0.75rem!important'
            },
            '& textarea': {
              padding: '0!important'
            }
          }}
          error={!!get(formState.errors, name)}
          helperText={note || ''}
        />
      );

    case 'country-dropdown': {
      return (
        <Controller
          name={name}
          control={control}
          defaultValue=""
          rules={config}
          render={({ field }) => (
            <Select
              error={!!get(formState.errors, name)}
              sx={{ width: '100%' }}
              {...field}
              displayEmpty
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>Select {label}</em>;
                }

                return selected;
              }}
            >
              <MenuItem disabled value="">
                <em>Select {label}</em>
              </MenuItem>
              {countryNames.map((item) => (
                <MenuItem key={item.key} value={item.key}>
                  {item.value}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      );
    }
    case 'select': {
      return (
        <Controller
          name={name}
          control={control}
          defaultValue={value}
          rules={config}
          render={({ field }) => (
            <Select
              error={!!get(formState.errors, name)}
              sx={{ width: '100%' }}
              {...field}
              displayEmpty
              disabled={disabled}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>Select {label}</em>;
                }

                return selected;
              }}
              onChange={(e) => {
                field.onChange(e.target.value);
                handleDropdownChange(name, e.target.value);
              }}
            >
              <MenuItem disabled value="">
                <em>Select {label}</em>
              </MenuItem>
              {options.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      );
    }
    case 'file':
      return (
        <Box style={{ display: 'flex' }}>
          <TextField
            sx={{ width: '100%' }}
            error={!!get(formState.errors, name)}
            key={`input-${name}_file_name`}
            className={classes.choseFile}
            value={inputValue}
          />

          <FormLabel htmlFor={name}>
            <Controller
              control={control}
              name={name}
              rules={{ validate: () => validateFile() }}
              render={({ field: { value: param, onChange, ...field } }) => {
                return (
                  <Input
                    {...field}
                    value={param?.fileName}
                    onChange={(event) => {
                      const input = event.target as HTMLInputElement;
                      if (!input.files?.length) {
                        return;
                      }
                      const file = input.files[0];
                      onChange(file);
                      attachFile(event as ChangeEvent<HTMLInputElement>);
                    }}
                    type="file"
                    id={name}
                    style={{ display: 'none' }}
                  />
                );
              }}
            />

            <IconButton className={classes.choseFileBtn} component="span">
              <FontAwesomeIcon icon={faPaperclip} size="sm" />
            </IconButton>
          </FormLabel>
        </Box>
      );
    case 'datetime':
      // If retention is set, then set the min date to the date picker
      // Note: Applies to all fields regardless of mandatory status
      if (retention && retention > 0) {
        const startDate = dayjs().subtract(retention, 'day');

        return (
          <Controller
            name={name}
            control={control}
            rules={config}
            render={({ field: { onChange, value: param } }) => {
              return (
                <DateTimePicker
                  className="dateTimePicker"
                  sx={{ width: '100%' }}
                  views={['year', 'day', 'hours', 'minutes']}
                  onChange={(newValue: Dayjs | null) => {
                    if (!newValue) {
                      onChange(null);
                      clearErrors(name);
                      return;
                    }
                    clearErrors(name);
                    // Validation for Sophos EDR: only allow past 24 hours
                    if (selectedSourceTypeId === SOURCE_TYPE.SOPHOS_EDR) {
                      const now = dayjs();
                      const diff = newValue.diff(now, 'hour', true);

                      // Invalid: more than 24 hours old or in the future
                      if (diff < -24 || diff > 0) {
                        setError(name, {
                          type: 'manual',
                          message:
                            'The ingest-from date must be within the past 24 hours.'
                        });
                        return;
                      }
                    }

                    // valid, update the value
                    onChange(newValue);
                  }}
                  slotProps={{
                    textField: {
                      error: !!get(formState.errors, name)
                    }
                  }}
                  value={param ? dayjs(param) : null}
                  defaultValue={param ? dayjs(param) : null}
                  minDate={startDate}
                  disableFuture
                  ampm={false}
                  disabled={disabled}
                  format={YEAR_MONTH_DAY_TIME_FORMAT2}
                />
              );
            }}
          />
        );
      }
      return null;
    case 'date':
      // Certificate never expires status informing to the user

      if (
        (name === 'expires' || name === 'backup_expires') &&
        softExpiry &&
        nonMandatory
      ) {
        return (
          <TextField
            sx={{ width: '100%' }}
            type="text"
            value={msg}
            slotProps={{ input: { readOnly: true } }}
          />
        );
      }

      // Auto-populated readonly expires field for Azure
      if (name === 'expires' && isAzureSource) {
        return (
          <Controller
            name={name}
            control={control}
            rules={config}
            render={({ field: { value: param } }) => {
              return (
                <DatePicker
                  className="dateTimePicker"
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      backgroundColor: 'action.disabledBackground'
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.disabled',
                      WebkitTextFillColor: 'text.disabled'
                    }
                  }}
                  onChange={() => {
                    // read-only: prevent any changes
                  }}
                  readOnly
                  slotProps={{
                    textField: {
                      error: !!formState.errors[name],
                      slotProps: { input: { readOnly: true } }
                    }
                  }}
                  value={param ? dayjs(param) : null}
                  defaultValue={param ? dayjs(param) : null}
                />
              );
            }}
          />
        );
      }

      if (
        !retention &&
        (name === 'expires' || name === 'certificate_expires')
      ) {
        return (
          <Controller
            name={name}
            control={control}
            rules={config}
            render={({ field: { onChange, value: param } }) => {
              return (
                <DatePicker
                  className="dateTimePicker"
                  sx={{ width: '100%' }}
                  onChange={onChange}
                  slotProps={{
                    textField: {
                      error: !!formState.errors[name]
                    }
                  }}
                  value={param ? dayjs(param) : null}
                  defaultValue={param ? dayjs(param) : null}
                  shouldDisableDate={(date) => {
                    return date.isSameOrBefore(dayjs(), 'day'); // disables today and all past dates
                  }}
                />
              );
            }}
          />
        );
      }
      return (
        <Controller
          name={name}
          control={control}
          rules={config}
          render={({ field: { onChange, value: param } }) => {
            return (
              <DatePicker
                sx={{ width: '100%' }}
                slotProps={{
                  textField: {
                    error: !!formState.errors[name]
                  }
                }}
                onChange={onChange}
                value={param ? dayjs(param) : null}
                defaultValue={param ? dayjs(param) : null}
              />
            );
          }}
        />
      );

    case 'password':
      // Special handling for Azure storage connection string
      if (name === 'storage_acct_conn_string' && isAzureSource) {
        return (
          <OutlinedInput
            {...register(name, {
              ...config,
              validate: (valPass) => {
                if (!valPass) return true; // Allow empty if not required
                const parsed = parseAzureConnectionString(valPass);
                return parsed !== null || 'Invalid storage connection string.';
              }
            })}
            error={!!get(formState.errors, name)}
            sx={{ width: '100%' }}
            id={name}
            type={showPassword ? 'text' : 'password'}
            disabled={disabled}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? (
                    <FontAwesomeIcon size="xs" icon={faEyeSlash} />
                  ) : (
                    <FontAwesomeIcon size="xs" icon={faEye} />
                  )}
                </IconButton>
              </InputAdornment>
            }
          />
        );
      }

      // Auto-populated readonly field for Azure SAS token
      if (name === 'sas_token' && isAzureSource) {
        return (
          <OutlinedInput
            {...register(name, config)}
            error={!!get(formState.errors, name)}
            sx={{
              width: '100%',
              backgroundColor: 'action.disabledBackground',
              '& .MuiInputBase-input': {
                color: 'text.disabled',
                WebkitTextFillColor: 'text.disabled'
              }
            }}
            id={name}
            type={showPassword ? 'text' : 'password'}
            disabled={disabled}
            slotProps={{ input: { readOnly: true } }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? (
                    <FontAwesomeIcon size="xs" icon={faEyeSlash} />
                  ) : (
                    <FontAwesomeIcon size="xs" icon={faEye} />
                  )}
                </IconButton>
              </InputAdornment>
            }
          />
        );
      }

      // Regular password field
      return (
        <OutlinedInput
          {...register(name, config)}
          error={!!get(formState.errors, name)}
          sx={{ width: '100%' }}
          id={name}
          type={showPassword ? 'text' : 'password'}
          disabled={disabled}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? (
                  <FontAwesomeIcon size="xs" icon={faEyeSlash} />
                ) : (
                  <FontAwesomeIcon size="xs" icon={faEye} />
                )}
              </IconButton>
            </InputAdornment>
          }
        />
      );
    case 'email':
      return (
        <TextField
          type="text"
          sx={{ width: '100%' }}
          error={!!get(formState.errors, name)}
          {...register(name, {
            required: !nonMandatory,
            validate: (param) =>
              !param || isValidEmail(param) || 'Invalid email format'
          })}
        />
      );
    case 'number':
      return (
        <TextField
          sx={{ width: '100%' }}
          type="number"
          error={!!get(formState.errors, name)}
          {...register(name, config)}
        />
      );
    case 'autocomplete': {
      const availableOptions = options || [];
      // Only add SELECT_ALL_OPTION if showSelectAll flag is true
      const allOptionsWithSelectAll = showSelectAll
        ? [SELECT_ALL_OPTION, ...availableOptions]
        : availableOptions;

      return (
        <Controller
          name={name}
          control={control}
          defaultValue={value || []}
          rules={config}
          render={({ field }) => {
            // Calculate selection state
            const selectedValues = field.value || [];
            const allSelected =
              availableOptions.length > 0 &&
              availableOptions.every((opt: AutocompleteOption) => {
                const optValue = getAutocompleteOptionValue(opt);
                return selectedValues.some(
                  (sel: AutocompleteOption) =>
                    getAutocompleteOptionValue(sel) === optValue
                );
              });
            const someSelected = selectedValues.length > 0 && !allSelected;

            // Handle selection change
            const handleChange = (
              _event: unknown,
              newValue: AutocompleteOption[]
            ) => {
              // Check if SELECT_ALL_OPTION is in the new value
              const hasSelectAll = newValue.some(
                (option) =>
                  getAutocompleteOptionValue(option) === SELECT_ALL_OPTION
              );

              if (hasSelectAll) {
                // User clicked "Select All"
                if (allSelected) {
                  // All items are currently selected -> deselect all
                  field.onChange([]);
                  // Call optional onFieldChange callback
                  if (onFieldChange) {
                    onFieldChange([]);
                  }
                } else {
                  // Not all items selected -> select all
                  field.onChange(availableOptions);
                  // Call optional onFieldChange callback
                  if (onFieldChange) {
                    onFieldChange(availableOptions);
                  }
                }
              } else {
                // Normal selection change -> filter out SELECT_ALL_OPTION from values
                const filtered = newValue.filter(
                  (option) =>
                    getAutocompleteOptionValue(option) !== SELECT_ALL_OPTION
                );
                field.onChange(filtered);
                // Call optional onFieldChange callback
                if (onFieldChange) {
                  onFieldChange(filtered);
                }
              }
            };

            return (
              <Autocomplete
                {...field}
                multiple
                limitTags={3}
                options={allOptionsWithSelectAll}
                disableCloseOnSelect
                isOptionEqualToValue={(option, val) => {
                  return (
                    getAutocompleteOptionValue(option) ===
                    getAutocompleteOptionValue(val)
                  );
                }}
                value={field.value || []}
                onChange={handleChange}
                getOptionLabel={(option) => {
                  if (
                    getAutocompleteOptionValue(option) === SELECT_ALL_OPTION
                  ) {
                    return 'Select All';
                  }
                  return getAutocompleteOptionLabel(option);
                }}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;

                  // Special rendering for SELECT_ALL_OPTION with three-state checkbox
                  if (
                    getAutocompleteOptionValue(option) === SELECT_ALL_OPTION
                  ) {
                    return (
                      <li key={key} {...optionProps}>
                        <Checkbox
                          icon={
                            <FontAwesomeIcon icon={faSquare} fontSize="small" />
                          }
                          checkedIcon={
                            <FontAwesomeIcon
                              icon={faCheckSquare}
                              fontSize="small"
                            />
                          }
                          indeterminateIcon={
                            <FontAwesomeIcon
                              icon={faSquareMinus}
                              fontSize="small"
                            />
                          }
                          style={{ marginRight: 8 }}
                          checked={allSelected}
                          indeterminate={someSelected}
                          disableRipple
                        />
                        Select All
                      </li>
                    );
                  }

                  // Regular option rendering
                  return (
                    <li key={key} {...optionProps}>
                      {getAutocompleteOptionLabel(option)}
                    </li>
                  );
                }}
                renderValue={(val, getItemProps) =>
                  val.map((option, index) => {
                    const { key, ...chipProps } = getItemProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={getAutocompleteOptionLabel(option)}
                        {...chipProps}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!get(formState.errors, name)}
                    helperText={note || ''}
                    disabled={disabled}
                  />
                )}
              />
            );
          }}
        />
      );
    }
    case 'toggle':
      if (mode !== 'Edit') {
        return null;
      }

      return (
        <Controller
          name={name}
          control={control}
          defaultValue={value}
          render={({ field }) => {
            // Extract the data provider path to check if it's selected
            // e.g., "data_provider.O365.active" -> "data_provider.O365.selected"
            let isProviderSelected = true;
            let isToggleDisabled = disabled;

            if (name.includes('data_provider.') && name.includes('.active')) {
              const selectedFieldName = name.replace('.active', '.selected');
              const selectedValue = getValues(selectedFieldName);
              isProviderSelected = !!selectedValue;

              // Check if this is a saved Microsoft provider (checkbox is hidden)
              let isSavedProvider = false;
              if (
                selectedSourceTypeId === SOURCE_TYPE.MICROSOFT &&
                selectedSourceDetails?.ms_data_providers
              ) {
                // Extract provider name from path
                // e.g., "data_provider.O365.active" -> "O365"
                // e.g., "data_provider.MicrosoftEntraID.GraphAPI.active" -> "GraphAPI"
                const pathParts = name.split('.');
                let providerName = '';

                if (pathParts.length === 3) {
                  // Simple case: data_provider.O365.active
                  providerName = pathParts[1];
                } else if (pathParts.length === 4) {
                  // Nested case: data_provider.MicrosoftEntraID.GraphAPI.active
                  providerName = pathParts[2];
                }

                // Check if this provider is in the saved list
                const savedProviders = selectedSourceDetails.ms_data_providers
                  .map((p) => p?.connection_inputs?.name)
                  .filter(Boolean);

                isSavedProvider = savedProviders.includes(providerName);
              }

              // Logic for enabling/disabling toggle:
              // 1. If provider is saved (existing): respect the disabled prop (for thumbprint validation)
              // 2. If provider is newly selected: always enable (ignore disabled prop)
              // 3. If provider is not selected and not saved: disable
              if (isSavedProvider) {
                // For saved providers, respect the disabled prop (thumbprint validation)
                isToggleDisabled = disabled;
              } else if (isProviderSelected) {
                // For newly selected providers, always enable
                isToggleDisabled = false;
              } else {
                // Provider is neither saved nor selected: disable
                isToggleDisabled = true;
              }
            }

            return (
              <Switch
                {...field}
                checked={!!field.value}
                onChange={(e) => {
                  const { checked } = e.target;
                  field.onChange(checked);
                }}
                disabled={isToggleDisabled}
                color={field.value ? 'success' : 'error'}
              />
            );
          }}
        />
      );

    default:
      return (
        <TextField
          sx={{ width: '100%' }}
          error={!!get(formState.errors, name)}
          {...register(name, config)}
        />
      );
  }
};
export default DynamicFormControl;
